import type { AreaGuide } from "./types"

export const BRISTOL_AREA_GUIDES: AreaGuide[] = [
  {
    slug: "clifton",
    name: "Clifton",
    location: "bristol",
    tagline: "Georgian elegance meets village charm",
    description:
      "Clifton is one of Bristol's most sought-after neighbourhoods, prized for its Georgian architecture, independent shops, and proximity to both the Downs and the city centre. The area blends period grandeur with a strong community feel, attracting professionals, families, and downsizers drawn to its tree-lined streets and cultural scene.\n\nProperty ranges from grand Georgian townhouses on Royal York Crescent to period conversions and garden flats. Clifton Village offers boutique shopping, cafes, and restaurants, while the Suspension Bridge and Avon Gorge provide a dramatic natural backdrop.",
    avgPrice: "£550,000",
    priceRange: "£250,000 - £2,500,000",
    propertyTypes: ["Georgian townhouses", "Period conversions", "Garden flats", "Mews houses"],
    transport: {
      nearestStation: "Clifton Down (0.5 miles)",
      commuteTime: "10 mins to Temple Meads by train",
      busRoutes: "8, 9, U1 to city centre",
      parking: "Residents' permit zones (RPZ)",
    },
    lifestyle: {
      restaurants: "Primrose Cafe, Wilsons, The Ivy Clifton Brasserie",
      schools: "Clifton College, Clifton High School, Christchurch Primary",
      greenSpaces: "The Downs (400 acres), Avon Gorge, Clifton Observatory",
      shopping: "Clifton Village independents, Whiteladies Road",
    },
    highlights: [
      "Clifton Suspension Bridge and Avon Gorge",
      "Clifton Village independent shops and cafes",
      "The Downs — 400 acres of open green space",
      "Strong community feel with regular local events",
      "Period architecture across every street",
    ],
    bestFor: ["Professionals", "Families", "Downsizers"],
    imageUrl: "/images/areas/bristol/clifton-800w.webp",
  },
  {
    slug: "redland",
    name: "Redland",
    location: "bristol",
    tagline: "Leafy streets and a strong community",
    description:
      "Redland is a popular residential area between Clifton and Bishopston, known for its Victorian and Edwardian homes, tree-lined streets, and excellent schools. It has a villagey feel with a strong community spirit, local shops, and easy access to the Downs.\n\nThe area attracts young families and professionals who want period character without Clifton's price premium. Redland Green provides a local park, and the high street has a growing selection of independent cafes and restaurants.",
    avgPrice: "£475,000",
    priceRange: "£220,000 - £1,200,000",
    propertyTypes: ["Victorian terraces", "Edwardian semis", "Period conversions", "Family houses"],
    transport: {
      nearestStation: "Redland (on MetroWest line)",
      commuteTime: "8 mins to Temple Meads by train",
      busRoutes: "9, 72, 73 to city centre",
      parking: "Mix of permit and unrestricted streets",
    },
    lifestyle: {
      restaurants: "The Kensington Arms, Poco Tapas Bar",
      schools: "Redland Green School, Cotham School, Bishop Road Primary",
      greenSpaces: "Redland Green, The Downs (nearby)",
      shopping: "Redland high street independents",
    },
    highlights: [
      "Some of Bristol's best state schools",
      "Victorian and Edwardian period homes",
      "Redland Green park and community events",
      "Walking distance to Whiteladies Road and The Downs",
      "Strong rental demand from University of Bristol students",
    ],
    bestFor: ["Families", "Professionals", "Investors"],
    imageUrl: "/images/areas/bristol/redland-800w.webp",
  },
  {
    slug: "bishopston",
    name: "Bishopston",
    location: "bristol",
    tagline: "Family-friendly with Gloucester Road on the doorstep",
    description:
      "Bishopston is one of Bristol's most family-friendly neighbourhoods, centred around Gloucester Road — the longest street of independent shops in the UK. Victorian terraces and Edwardian semis line quiet residential streets, while the high street provides everything from artisan bakeries to vintage shops.\n\nThe area has excellent schools, a thriving community, and strong transport links. It's popular with young families upgrading from flats in the city centre and professionals who want a neighbourhood with character.",
    avgPrice: "£450,000",
    priceRange: "£200,000 - £900,000",
    propertyTypes: ["Victorian terraces", "Edwardian semis", "Period conversions"],
    transport: {
      nearestStation: "Montpelier (on MetroWest line)",
      commuteTime: "15 mins to Temple Meads by bus",
      busRoutes: "75, 76, 77 along Gloucester Road",
      parking: "Mostly unrestricted residential streets",
    },
    lifestyle: {
      restaurants: "The Gallimaufry, Flour & Ash, Joe's Bakery",
      schools: "Bishop Road Primary, Ashley Down Primary, Cotham School",
      greenSpaces: "St Andrew's Park, Horfield Common",
      shopping: "Gloucester Road independents (over 150 shops)",
    },
    highlights: [
      "Gloucester Road — UK's longest independent high street",
      "Outstanding primary schools",
      "Strong community with regular street events",
      "Excellent period housing stock",
      "Easy cycle route to city centre",
    ],
    bestFor: ["Families", "First-time buyers", "Young professionals"],
    imageUrl: "/images/areas/bristol/bishopston-800w.webp",
  },
  {
    slug: "southville",
    name: "Southville",
    location: "bristol",
    tagline: "Creative south Bristol with a village soul",
    description:
      "Southville has transformed from a working-class neighbourhood into one of Bristol's most desirable areas, popular with creative professionals and young families. North Street is the social hub, packed with independent shops, cafes, pubs, and the Tobacco Factory arts venue.\n\nThe area combines Victorian terraces with a strong sense of community and easy access to the harbourside. Property prices have risen sharply but remain more accessible than Clifton or Redland, making it popular with buyers looking for character and culture.",
    avgPrice: "£400,000",
    priceRange: "£200,000 - £750,000",
    propertyTypes: ["Victorian terraces", "Cottage-style homes", "New-build apartments"],
    transport: {
      nearestStation: "Parson Street (1 mile)",
      commuteTime: "10 mins to city centre by bike",
      busRoutes: "24, 75, 76 to city centre",
      parking: "Increasingly permit-controlled",
    },
    lifestyle: {
      restaurants: "The Spotted Cow, Pizzarova, Mark's Bread",
      schools: "Southville Primary, Ashton Gate Primary",
      greenSpaces: "Greville Smyth Park, Bedminster Green",
      shopping: "North Street independents, Tobacco Factory market",
    },
    highlights: [
      "Tobacco Factory theatre and arts venue",
      "North Street independent shops and restaurants",
      "Walking distance to harbourside",
      "Strong creative community",
      "Regular farmers' markets and street events",
    ],
    bestFor: ["Young professionals", "Creative types", "Families"],
    imageUrl: "/images/areas/bristol/southville-800w.webp",
  },
  {
    slug: "stoke-bishop",
    name: "Stoke Bishop",
    location: "bristol",
    tagline: "Premium family living on the edge of the Downs",
    description:
      "Stoke Bishop is one of Bristol's most exclusive residential areas, sitting between The Downs and the Avon Gorge. Large detached family homes on generous plots characterise the area, with many properties offering views across the gorge or direct access to the Downs.\n\nThe area is quieter and more suburban than neighbouring Clifton, attracting established families and professionals who want space, green surroundings, and proximity to some of the city's best schools.",
    avgPrice: "£750,000",
    priceRange: "£400,000 - £3,000,000",
    propertyTypes: ["Detached family homes", "Large period properties", "Executive homes"],
    transport: {
      nearestStation: "Sea Mills (0.5 miles)",
      commuteTime: "15 mins to Temple Meads by car",
      busRoutes: "4, 505 to city centre",
      parking: "Off-street parking standard",
    },
    lifestyle: {
      restaurants: "The Downs Cafe, local options on Stoke Hill",
      schools: "Stoke Bishop Primary, Badminton School, QEH",
      greenSpaces: "The Downs, Avon Gorge nature reserve",
      shopping: "Local convenience shops, Westbury-on-Trym village nearby",
    },
    highlights: [
      "Direct access to The Downs and Avon Gorge",
      "Some of Bristol's largest family homes",
      "Proximity to top independent schools",
      "Quiet, green, suburban feel within the city",
      "Strong capital growth over the past decade",
    ],
    bestFor: ["Families", "Upsizers", "Professionals"],
    imageUrl: "/images/areas/bristol/stoke-bishop-800w.webp",
  },
  {
    slug: "henleaze",
    name: "Henleaze",
    location: "bristol",
    tagline: "Village shopping and the famous lake",
    description:
      "Henleaze is a desirable residential area in north Bristol, known for its village-like high street, Henleaze Swimming Lake (a rare outdoor freshwater pool), and excellent schools. The area has a strong family demographic and a relaxed, community-oriented atmosphere.\n\nProperty is predominantly interwar and post-war semis and detached houses, offering more space per pound than Clifton or Redland. The high street has a good mix of independent shops, cafes, and restaurants.",
    avgPrice: "£500,000",
    priceRange: "£250,000 - £1,000,000",
    propertyTypes: ["1930s semis", "Detached houses", "Bungalows", "Period conversions"],
    transport: {
      nearestStation: "No direct rail (bus to Temple Meads)",
      commuteTime: "20 mins to city centre by bus",
      busRoutes: "1, 2, 3 to city centre",
      parking: "Generally unrestricted",
    },
    lifestyle: {
      restaurants: "Fishers, Wellsway Cafe, The Henleaze Arms",
      schools: "Henleaze Junior School, Westbury Park Primary, Badminton School",
      greenSpaces: "Henleaze Lake, Canford Park, The Downs (nearby)",
      shopping: "Henleaze Road independents",
    },
    highlights: [
      "Henleaze Swimming Lake — one of few in the UK",
      "Village-feel high street with independents",
      "Excellent state and private schools",
      "Generous plot sizes compared to inner suburbs",
      "Strong sense of community",
    ],
    bestFor: ["Families", "Downsizers", "Retirees"],
    imageUrl: "/images/areas/bristol/henleaze-800w.webp",
  },
  {
    slug: "harbourside",
    name: "Harbourside",
    location: "bristol",
    tagline: "Waterfront living in the heart of Bristol",
    description:
      "Bristol's Harbourside is the city's cultural and leisure hub, centred around the Floating Harbour. The area combines converted industrial buildings with modern apartment developments, offering waterfront living within walking distance of the city centre.\n\nThe Harbourside is home to M Shed, Arnolfini, We The Curious, and a growing number of restaurants and bars. It's popular with young professionals and investors looking for low-maintenance city living with character.",
    avgPrice: "£325,000",
    priceRange: "£180,000 - £750,000",
    propertyTypes: ["Modern apartments", "Converted warehouses", "Dockside new builds"],
    transport: {
      nearestStation: "Temple Meads (0.5 miles)",
      commuteTime: "Walking distance to city centre",
      busRoutes: "Multiple routes from city centre",
      parking: "Underground parking in developments; limited on-street",
    },
    lifestyle: {
      restaurants: "Riverstation, The Olive Shed, Wapping Wharf restaurants",
      schools: "Cathedral Primary, Southville Primary (nearby)",
      greenSpaces: "Castle Park, Queen Square, harbour walks",
      shopping: "Wapping Wharf cargo containers, Cabot Circus nearby",
    },
    highlights: [
      "Waterfront views along the Floating Harbour",
      "Cultural venues: M Shed, Arnolfini, We The Curious",
      "Wapping Wharf food and drink scene",
      "Walking distance to Temple Meads and city centre",
      "Strong rental demand and capital growth",
    ],
    bestFor: ["Young professionals", "Investors", "Couples"],
    imageUrl: "/images/areas/bristol/harbourside-800w.webp",
  },
  {
    slug: "westbury-on-trym",
    name: "Westbury-on-Trym",
    location: "bristol",
    tagline: "Historic village with a thriving high street",
    description:
      "Westbury-on-Trym is a historic village within Bristol, centred around a medieval church and a lively high street. The area has a strong community identity, excellent local shops, and a mix of period and modern housing.\n\nIt's popular with families and retirees who want village-style living with city convenience. The high street has banks, independent shops, restaurants, and a Saturday market. Nearby Blaise Castle Estate provides extensive green space.",
    avgPrice: "£450,000",
    priceRange: "£200,000 - £1,100,000",
    propertyTypes: ["1930s semis", "Detached houses", "Period cottages", "Modern developments"],
    transport: {
      nearestStation: "Sea Mills (1 mile)",
      commuteTime: "20 mins to city centre by bus",
      busRoutes: "1, 2, 3, 4 to city centre",
      parking: "Mix of on-street and off-street",
    },
    lifestyle: {
      restaurants: "The Post Office, Village Fish Bar, Hobbs House Bakery",
      schools: "Westbury Park Primary, Elmlea Schools, Bristol Grammar (nearby)",
      greenSpaces: "Blaise Castle Estate, Canford Park",
      shopping: "Westbury-on-Trym village high street",
    },
    highlights: [
      "Medieval village centre with character",
      "Thriving high street with independent shops",
      "Blaise Castle Estate on the doorstep",
      "Strong schools in the catchment",
      "Regular community events and markets",
    ],
    bestFor: ["Families", "Retirees", "Downsizers"],
    imageUrl: "/images/areas/bristol/westbury-on-trym-800w.webp",
  },
]
